import { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  CalendarDays, MapPin, Clock, DollarSign, CheckCircle, Loader2,
  Ruler, Tv, Wifi, Coffee, UtensilsCrossed, Armchair,
  ShieldCheck, AlertTriangle, CreditCard, KeyRound, Camera, Phone,
  ArrowRight, ArrowLeft, User, Mail, Building, MapPinned,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import API from "@/apiConfig";

/* ─── Real Community Room gallery from R2 sovereign storage ──────────────── */
const R2_BASE = "https://assets.wordaliveglobal.org/page_requests_attachments/Community_Room";
const GALLERY = [
  { src: `${R2_BASE}/CommunityRoom_Inside_From_Front_Door.jpg`, caption: "Main Hall — View from Entrance" },
  { src: `${R2_BASE}/CommunityRoom_Inside_From_Front_Door_2.jpg`, caption: "Main Hall — Alternate View" },
  { src: `${R2_BASE}/CommunityRoom_Inside_Back_Half.jpg`, caption: "Back Half — Seating Area" },
  { src: `${R2_BASE}/CommunityRoom_Tables_Chairs.jpg`, caption: "Tables & Chairs Setup" },
  { src: `${R2_BASE}/CommunityRoom_Kitchen.jpg`, caption: "Kitchen Area" },
  { src: `${R2_BASE}/CommunityRoom_Rest_Rooms.jpg`, caption: "Restrooms" },
];

const HERO_IMG = `${R2_BASE}/CommunityRoom_Inside_From_Front_Door.jpg`;

const AMENITIES = [
  { icon: Ruler, label: "~2,200 sq ft open space" },
  { icon: Armchair, label: "~14 tables, ~120 chairs" },
  { icon: Tv, label: "TV with HDMI" },
  { icon: Wifi, label: "WiFi Available" },
  { icon: Coffee, label: "Coffee Maker" },
  { icon: UtensilsCrossed, label: "Refrigerator, Microwave, Sink" },
];

const REGULATIONS = [
  "Nothing may be attached to the walls (no staples, tacks, tape, etc.).",
  "Rollable backdrop may NOT be moved or used by renters.",
  "No sub-renting to third parties.",
  "Renter is fully responsible for guest conduct and any damage.",
  "Smoking strictly prohibited inside (including e-cigarettes). Allowed only in designated outdoor areas.",
  "Renter must set up and fully restore the room to original condition.",
  "Return all tables, chairs, and dishes to their places. Sweep floors and remove all trash to the dumpster in front of The Master's Blend (not behind it). Cleaning supplies are in the closet and under the kitchen sink.",
];

/* ─── Auto-pricing helper ────────────────────────────────────────────────── */
const getPriceForDate = (date) => {
  if (!date) return null;
  const day = date.getDay();
  // Sunday (0) and Saturday (6) are Weekend rate $250
  if (day === 0 || day === 6) return 250;
  return 150;                  // Mon–Fri
};

const getDayLabel = (date) => {
  if (!date) return "";
  return date.toLocaleDateString("en-US", { weekday: "long" });
};

const isSunday = (date) => date.getDay() === 0;

const generateTimeOptions = () => {
  const times = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const isPM = h >= 12;
      const displayH = h % 12 === 0 ? 12 : h % 12;
      const displayM = m === 0 ? "00" : m;
      const ampm = isPM ? "PM" : "AM";
      times.push(`${displayH}:${displayM} ${ampm}`);
    }
  }
  return times;
};
const TIME_OPTIONS = generateTimeOptions();

const DEPOSIT_OPTIONS = [
  { id: "paypal", label: "PayPal or Credit/Debit Card", sublabel: "$50 deposit after approval", icon: CreditCard },
];

export default function RentalPage() {
  const [bookingOpen, setBookingOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    purpose: "", date: "", startTime: "10:00 AM", endTime: "2:00 PM", guests: "", message: "",
    first_name: "", last_name: "", organization: "",
    street: "", street2: "", city: "", state: "", zip: "",
    phone: "", email: "",
  });
  const [dateObj, setDateObj] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);
  const [pendingDates, setPendingDates] = useState([]);

  const price = dateObj ? getPriceForDate(dateObj) : null;
  const isSelectedDatePending = form.date && pendingDates.includes(form.date);
  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleDateSelect = (date) => {
    if (!date) return;
    // if (isSunday(date)) return; // Sunday guard bypassed (re-enabled April 2026)
    setDateObj(date);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    updateField("date", `${y}-${m}-${d}`);
  };

  const validateStep1 = () => {
    if (!form.purpose.trim()) { toast.error("Please describe the purpose of your event."); return false; }
    if (!form.date) { toast.error("Please select a date."); return false; }
    return true;
  };

  const validateStep2 = () => {
    if (!form.first_name.trim() || !form.last_name.trim()) { toast.error("First and last name are required."); return false; }
    if (!form.street.trim() || !form.city.trim() || !form.state.trim() || !form.zip.trim()) { toast.error("Please complete your address."); return false; }
    if (!form.phone.trim()) { toast.error("Phone number is required."); return false; }
    if (!form.email.trim()) { toast.error("Email is required."); return false; }
    return true;
  };

  const goNext = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    setStep((s) => Math.min(s + 1, 3));
  };

  const goBack = () => setStep((s) => Math.max(s - 1, 1));

  const resetForm = () => {
    setSubmitted(false);
    setStep(1);
    setForm({
      purpose: "", date: "", time: "", guests: "", message: "",
      first_name: "", last_name: "", organization: "",
      street: "", street2: "", city: "", state: "", zip: "",
      phone: "", email: "",
    });
    setDateObj(null);
  };

  /* ─── Fetch blocked + pending dates on mount ─────────────────────────────── */
  useEffect(() => {
    axios.get(`${API}/rentals/blocked-dates`)
      .then((r) => {
        const data = r.data;
        if (Array.isArray(data)) {
          setBlockedDates(data);
        } else {
          setBlockedDates(data.blocked || []);
          setPendingDates(data.pending || []);
        }
      })
      .catch(() => {});
  }, []);

  /* ─── Shared submit function ─────────────────────────────────────────────── */
  const submitRentalRequest = async () => {
    setLoading(true);
    const fullName = `${form.first_name} ${form.last_name}`.trim();
    const addressBlock = [form.street, form.street2, `${form.city}, ${form.state} ${form.zip}`].filter(Boolean).join("\n");
    const notesParts = [];
    if (form.organization) notesParts.push(`Org: ${form.organization}`);
    notesParts.push(`Address:\n${addressBlock}`);
    if (form.message) notesParts.push(`Message: ${form.message}`);
    if (price) notesParts.push(`Quoted Price: $${price}`);
    try {
      await axios.post(`${API}/rental-requests`, {
        name: fullName,
        email: form.email,
        phone: form.phone,
        event_name: form.purpose,
        event_date: form.date,
        event_time: `${form.startTime} - ${form.endTime}`,
        guests: parseInt(form.guests) || 0,
        notes: notesParts.join("\n\n"),
        rental_amount: price || 0,
        payment_method: "paypal",
        paypal_order_id: "",
        deposit_amount: 0,
      });
      setSubmitted(true);
      toast.success("Booking request submitted! We'll be in touch.");
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to submit request. Please try again.";
      toast.error(detail);
      if (err.response?.status === 409) {
        setBlockedDates((prev) => [...prev, form.date]);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>

      {/* ─── Hero ─────────────────────────────────────────────────── */}
      <section className="relative min-h-[50vh] flex items-center" data-testid="rental-hero">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_IMG})` }}>
          <div className="absolute inset-0 bg-navy/85" />
        </div>
        <div className="relative z-10 w-full container-page py-20 text-center">
          <div className="gold-line-center mb-5" />
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mb-3" data-testid="rental-heading">
            The Community Room
          </h1>
          <p className="text-gold font-heading text-lg sm:text-xl mb-4">
            @ Word Alive Global Center
          </p>
          <p className="text-bone/70 text-sm flex items-center justify-center gap-2 mb-6">
            <MapPin className="h-4 w-4 text-gold shrink-0" />
            1128 W Panola St, Carthage, TX
          </p>
          <p className="text-bone/60 text-base max-w-2xl mx-auto leading-relaxed mb-8">
            A welcoming space for connection, celebration, and community events.
          </p>
          <Button
            data-testid="hero-request-booking-btn"
            className="btn-gold px-10 py-3 uppercase tracking-wider font-semibold text-sm"
            onClick={() => setBookingOpen(true)}
          >
            Request Booking
          </Button>
        </div>
      </section>

      {/* ─── Rental Note ──────────────────────────────────────────── */}
      <section className="bg-bone-dark border-b border-bone-dark">
        <div className="container-page py-4 text-center">
          <p className="text-navy/70 text-sm flex items-center justify-center gap-2">
            <Clock className="h-4 w-4 text-gold shrink-0" />
            Rental time includes setup, event, and cleanup. Access is restricted to the paid period only.
          </p>
        </div>
      </section>

      {/* ─── Pricing ──────────────────────────────────────────────── */}
      <section className="py-16 bg-bone" data-testid="rental-pricing">
        <div className="container-page">
          <div className="text-center mb-10">
            <div className="gold-line-center mb-4" />
            <h2 className="font-heading text-3xl text-navy">Rental Pricing</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
            {/* Weekdays */}
            <Card className="bg-white border border-bone-dark shadow-sm hover:shadow-md transition-shadow" data-testid="pricing-weekday">
              <CardContent className="p-6 text-center">
                <DollarSign className="h-6 w-6 text-gold mx-auto mb-3" />
                <h3 className="font-heading text-xl text-navy mb-1">Weekdays</h3>
                <p className="text-gray-500 text-xs mb-4">Mon–Fri</p>
                <p className="font-heading text-3xl text-navy">$150</p>
              </CardContent>
            </Card>
            {/* Weekends */}
            <Card className="bg-white border-2 border-gold/40 shadow-md" data-testid="pricing-weekend">
              <CardContent className="p-6 text-center">
                <Badge className="bg-gold/15 text-gold border border-gold/30 text-xs mb-3">Most Popular</Badge>
                <DollarSign className="h-6 w-6 text-gold mx-auto mb-3" />
                <h3 className="font-heading text-xl text-navy mb-1">Weekends</h3>
                <p className="text-gray-500 text-xs mb-4">Sat & Sun</p>
                <p className="font-heading text-3xl text-navy">$250</p>
              </CardContent>
            </Card>
          </div>
          <div className="text-center mt-10">
            <Button
              data-testid="pricing-request-booking-btn"
              className="btn-gold px-10 py-3 uppercase tracking-wider font-medium"
              onClick={() => setBookingOpen(true)}
            >
              Request Booking
            </Button>
          </div>
        </div>
      </section>

      {/* ─── Room Details & Amenities ─────────────────────────────── */}
      <section className="py-16 bg-white" data-testid="rental-amenities">
        <div className="container-page">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="gold-line mb-4" />
              <h2 className="font-heading text-3xl text-navy mb-6">Room Details</h2>
              <p className="text-gray-600 leading-relaxed mb-8">
                The Community Room offers approximately 2,200 square feet of versatile open space — ideal for
                receptions, meetings, showers, family gatherings, and community events. Everything you need for
                a smooth gathering is included.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {AMENITIES.map(({ icon: Icon, label }) => (
                  <div key={label} className="flex items-center gap-3 text-gray-700">
                    <div className="w-9 h-9 rounded-sm bg-bone flex items-center justify-center shrink-0">
                      <Icon className="h-4 w-4 text-gold" />
                    </div>
                    <span className="text-sm">{label}</span>
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-6">
                Two individual restrooms included. Cleaning supplies located in closet and under kitchen sink.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {GALLERY.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setLightboxIdx(i)}
                  className="relative overflow-hidden rounded-sm shadow-sm hover:shadow-md transition-shadow group cursor-pointer"
                  data-testid={`gallery-thumb-${i}`}
                >
                  <img src={img.src} alt={img.caption} className="w-full h-36 sm:h-44 object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                  <div className="absolute inset-0 bg-navy/0 group-hover:bg-navy/20 transition-colors flex items-center justify-center">
                    <Camera className="h-5 w-5 text-white opacity-0 group-hover:opacity-80 transition-opacity" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Payments & Policies ───────────────────────────────────── */}
      <section className="py-16 bg-bone" data-testid="rental-policies">
        <div className="container-page">
          <div className="text-center mb-10">
            <div className="gold-line-center mb-4" />
            <h2 className="font-heading text-3xl text-navy">Payment & Policies</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Payment */}
            <Card className="bg-white border border-bone-dark shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <CreditCard className="h-5 w-5 text-gold" />
                  <h3 className="font-heading text-lg text-navy">Payment Options</h3>
                </div>
                <ul className="space-y-3 text-sm text-gray-600">
                  <li className="flex gap-2">
                    <span className="text-gold mt-0.5 shrink-0">&#x2022;</span>
                    <span><strong className="text-navy">$50 nonrefundable deposit</strong> required to hold the date (deducted from total). Paid securely online via PayPal or credit/debit card.</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-gold mt-0.5 shrink-0">&#x2022;</span>
                    <span>Full payment due within <strong className="text-navy">10 business days</strong> of deposit.</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            {/* Cancellation & Keys */}
            <Card className="bg-white border border-bone-dark shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <KeyRound className="h-5 w-5 text-gold" />
                  <h3 className="font-heading text-lg text-navy">Cancellation & Key Pickup</h3>
                </div>
                <ul className="space-y-3 text-sm text-gray-600">
                  <li className="flex gap-2">
                    <span className="text-gold mt-0.5 shrink-0">&#x2022;</span>
                    <span>Full refund minus $50 deposit if canceled <strong className="text-navy">48+ hours in advance</strong>.</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-gold mt-0.5 shrink-0">&#x2022;</span>
                    <span>Key pickup: Only from <strong className="text-navy">The Master's Blend</strong> during business hours (day of event, or Friday before a weekend rental).</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ─── Regulations ───────────────────────────────────────────── */}
      <section className="py-16 bg-white" data-testid="rental-regulations">
        <div className="container-page max-w-3xl">
          <div className="text-center mb-10">
            <div className="gold-line-center mb-4" />
            <h2 className="font-heading text-3xl text-navy">Room Regulations</h2>
            <p className="text-gray-500 text-sm mt-2">Stewardship of shared space — please read carefully</p>
          </div>
          <div className="space-y-3">
            {REGULATIONS.map((rule, i) => (
              <div key={i} className="flex gap-3 items-start bg-bone rounded-sm p-4 border border-bone-dark" data-testid={`regulation-${i}`}>
                <ShieldCheck className="h-4 w-4 text-gold mt-0.5 shrink-0" />
                <p className="text-sm text-gray-700 leading-relaxed">{rule}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Photo Gallery ─────────────────────────────────────────── */}
      <section className="py-16 bg-bone" data-testid="rental-gallery">
        <div className="container-page">
          <div className="text-center mb-10">
            <div className="gold-line-center mb-4" />
            <h2 className="font-heading text-3xl text-navy">Gallery</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {GALLERY.map((img, i) => (
              <button
                key={i}
                onClick={() => setLightboxIdx(i)}
                className="relative overflow-hidden rounded-sm shadow-sm hover:shadow-md transition-shadow group cursor-pointer"
                data-testid={`gallery-item-${i}`}
              >
                <img src={img.src} alt={img.caption} className="w-full h-40 md:h-48 object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-navy/70 to-transparent p-3">
                  <p className="text-white text-xs font-medium">{img.caption}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Final CTA ──────────────────────────────────────────────── */}
      <section className="py-20 bg-navy text-white">
        <div className="container-page text-center">
          <div className="gold-line-center mb-5" />
          <h2 className="font-heading text-3xl sm:text-4xl mb-4">Ready to Reserve?</h2>
          <p className="text-white/60 text-base max-w-xl mx-auto leading-relaxed mb-8">
            Whether it's a family celebration, community gathering, or corporate event — The Community Room is here for you.
          </p>
          <Button
            data-testid="footer-request-booking-btn"
            className="btn-gold px-10 py-3 uppercase tracking-wider font-semibold text-sm"
            onClick={() => setBookingOpen(true)}
          >
            Request Booking
          </Button>
        </div>
      </section>

      {/* ─── Lightbox Dialog ──────────────────────────────────────── */}
      <Dialog open={lightboxIdx !== null} onOpenChange={() => setLightboxIdx(null)}>
        <DialogContent className="max-w-2xl p-2 bg-navy border-gold/20">
          <DialogHeader className="sr-only">
            <DialogTitle>Gallery Photo</DialogTitle>
            <DialogDescription>Community Room photo gallery</DialogDescription>
          </DialogHeader>
          {lightboxIdx !== null && (
            <div>
              <img src={GALLERY[lightboxIdx]?.src} alt={GALLERY[lightboxIdx]?.caption} className="w-full rounded-sm" />
              <p className="text-white/80 text-sm text-center mt-3 pb-2">{GALLERY[lightboxIdx]?.caption}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ─── Booking Request Modal (2-Step Wizard) ─────────────────── */}
      <Dialog open={bookingOpen} onOpenChange={(open) => { setBookingOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="booking-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-navy">Request Booking</DialogTitle>
            <DialogDescription className="text-gray-500 text-sm">
              The Community Room @ 1128 W Panola St, Carthage, TX
            </DialogDescription>
          </DialogHeader>

          {/* Progress indicator */}
          {!submitted && (
            <div className="flex items-center gap-2 mb-2" data-testid="wizard-progress">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center gap-2 flex-1">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                    step >= s ? "bg-gold text-navy" : "bg-bone-dark text-gray-400"
                  }`}>{s}</div>
                  {s < 3 && <div className={`flex-1 h-0.5 transition-colors ${step > s ? "bg-gold" : "bg-bone-dark"}`} />}
                </div>
              ))}
            </div>
          )}

          {submitted ? (
            <div className="text-center py-6" data-testid="booking-success">
              <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-3" />
              <p className="font-heading text-xl text-navy mb-2">Request Received!</p>
              <p className="text-gray-500 text-sm mb-4">We will review your request and reach out to confirm availability and payment details.</p>
              <Button className="btn-navy" onClick={() => { resetForm(); setBookingOpen(false); }}>Close</Button>
            </div>

          ) : step === 1 ? (
            /* ── Step 1: Event Details ──────────────────────────── */
            <div className="space-y-4" data-testid="wizard-step-1">
              <p className="font-heading text-base text-navy">Tell Us About Your Event</p>

              <div>
                <Label className="text-navy text-sm">Purpose of Event *</Label>
                <Textarea data-testid="booking-purpose" value={form.purpose} onChange={(e) => updateField("purpose", e.target.value)} rows={2} className="border-bone-dark focus:border-gold rounded-sm mt-1" placeholder="e.g. Birthday party, baby shower, community meeting..." />
              </div>

              <div>
                <Label className="text-navy text-sm">Date of Proposed Rental *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button data-testid="booking-date-trigger" variant="outline" className="w-full justify-start text-left border-bone-dark rounded-sm mt-1 text-sm">
                      <CalendarDays className="h-3.5 w-3.5 mr-2 text-gold" />
                      {form.date ? `${form.date} (${getDayLabel(dateObj)})` : "Select a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" onPointerDownOutside={(e) => e.preventDefault()}>
                    <Calendar
                      mode="single"
                      selected={dateObj}
                      onSelect={handleDateSelect}
                      disabled={[{ before: new Date() }, ...blockedDates.map(d => new Date(d + "T12:00:00"))]}
                      modifiers={{
                        pending: pendingDates.map(d => new Date(d + "T12:00:00")),
                      }}
                      modifiersClassNames={{
                        pending: "bg-amber-100 text-amber-800 font-medium rounded-full",
                      }}
                    />
                  </PopoverContent>
                </Popover>
                {/* Live pricing */}
                {dateObj && price !== null && (
                  <div className="mt-2 flex items-center gap-2 bg-bone rounded-sm px-3 py-2" data-testid="live-price">
                    <DollarSign className="h-4 w-4 text-gold shrink-0" />
                    <span className="text-sm text-navy font-medium">
                      Rental Fee: <strong className="text-gold">${price}</strong>
                      <span className="text-gray-500 font-normal ml-1">({getDayLabel(dateObj)} rate)</span>
                    </span>
                  </div>
                )}
                {/* Pending deposit warning */}
                {isSelectedDatePending && (
                  <div className="mt-2 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2" data-testid="pending-deposit-warning">
                    <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-800 leading-relaxed">
                      There is a pending deposit on this time slot. You may still submit your request, but we recommend trying a different date.
                    </p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-navy text-sm">Expected Guests</Label>
                  <Input data-testid="booking-guests" type="number" min="0" max="120" value={form.guests} onChange={(e) => updateField("guests", e.target.value)} className="border-bone-dark focus:border-gold rounded-sm mt-1" placeholder="~30" />
                </div>
                <div className="col-span-2 grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-navy text-sm">Start Time</Label>
                    <Select value={form.startTime} onValueChange={(v) => updateField("startTime", v)}>
                      <SelectTrigger className="mt-1 bg-white border-bone-dark focus:border-gold rounded-sm" data-testid="booking-start-time-select">
                        <SelectValue placeholder="Start Time" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {TIME_OPTIONS.map((t) => <SelectItem key={`start-${t}`} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-navy text-sm">End Time</Label>
                    <Select value={form.endTime} onValueChange={(v) => updateField("endTime", v)}>
                      <SelectTrigger className="mt-1 bg-white border-bone-dark focus:border-gold rounded-sm" data-testid="booking-end-time-select">
                        <SelectValue placeholder="End Time" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {TIME_OPTIONS.map((t) => <SelectItem key={`end-${t}`} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-navy text-sm">Additional Message</Label>
                <Textarea data-testid="booking-message" value={form.message} onChange={(e) => updateField("message", e.target.value)} rows={2} className="border-bone-dark focus:border-gold rounded-sm mt-1" placeholder="Any special requests or setup needs..." />
              </div>

              <Button data-testid="wizard-next-1" className="w-full btn-gold py-3 uppercase tracking-wider font-medium" onClick={goNext}>
                Next: Your Info <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>

          ) : step === 2 ? (
            /* ── Step 2: Contact & Address ─────────────────────── */
            <div className="space-y-4" data-testid="wizard-step-2">
              <p className="font-heading text-base text-navy">Your Contact & Address Info</p>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-navy text-sm">First Name *</Label>
                  <Input data-testid="booking-first-name" value={form.first_name} onChange={(e) => updateField("first_name", e.target.value)} className="border-bone-dark focus:border-gold rounded-sm mt-1" placeholder="First" />
                </div>
                <div>
                  <Label className="text-navy text-sm">Last Name *</Label>
                  <Input data-testid="booking-last-name" value={form.last_name} onChange={(e) => updateField("last_name", e.target.value)} className="border-bone-dark focus:border-gold rounded-sm mt-1" placeholder="Last" />
                </div>
              </div>

              <div>
                <Label className="text-navy text-sm">Organization (optional)</Label>
                <Input data-testid="booking-org" value={form.organization} onChange={(e) => updateField("organization", e.target.value)} className="border-bone-dark focus:border-gold rounded-sm mt-1" placeholder="Company or group name" />
              </div>

              <div>
                <Label className="text-navy text-sm">Street Address *</Label>
                <Input data-testid="booking-street" value={form.street} onChange={(e) => updateField("street", e.target.value)} className="border-bone-dark focus:border-gold rounded-sm mt-1" placeholder="123 Main St" />
              </div>
              <div>
                <Label className="text-navy text-sm">Other Address (optional)</Label>
                <Input data-testid="booking-street2" value={form.street2} onChange={(e) => updateField("street2", e.target.value)} className="border-bone-dark focus:border-gold rounded-sm mt-1" placeholder="Apt, Suite, Unit" />
              </div>

              <div className="grid grid-cols-5 gap-3">
                <div className="col-span-2">
                  <Label className="text-navy text-sm">City *</Label>
                  <Input data-testid="booking-city" value={form.city} onChange={(e) => updateField("city", e.target.value)} className="border-bone-dark focus:border-gold rounded-sm mt-1" placeholder="Carthage" />
                </div>
                <div className="col-span-1">
                  <Label className="text-navy text-sm">State *</Label>
                  <Input data-testid="booking-state" value={form.state} onChange={(e) => updateField("state", e.target.value)} className="border-bone-dark focus:border-gold rounded-sm mt-1" placeholder="TX" maxLength={2} />
                </div>
                <div className="col-span-2">
                  <Label className="text-navy text-sm">Zip *</Label>
                  <Input data-testid="booking-zip" value={form.zip} onChange={(e) => updateField("zip", e.target.value)} className="border-bone-dark focus:border-gold rounded-sm mt-1" placeholder="75633" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-navy text-sm">Phone *</Label>
                  <Input data-testid="booking-phone" type="tel" value={form.phone} onChange={(e) => updateField("phone", e.target.value)} className="border-bone-dark focus:border-gold rounded-sm mt-1" placeholder="(903) 555-0100" />
                </div>
                <div>
                  <Label className="text-navy text-sm">Email *</Label>
                  <Input data-testid="booking-email" type="email" value={form.email} onChange={(e) => updateField("email", e.target.value)} className="border-bone-dark focus:border-gold rounded-sm mt-1" placeholder="you@email.com" />
                </div>
              </div>

              <div className="flex gap-3">
                <Button data-testid="wizard-back-2" variant="outline" className="flex-1 border-bone-dark text-navy" onClick={goBack}>
                  <ArrowLeft className="h-4 w-4 mr-2" /> Back
                </Button>
                <Button data-testid="wizard-next-2" className="flex-1 btn-gold uppercase tracking-wider font-medium" onClick={goNext}>
                  Review <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>

          ) : (
            /* ── Step 3: Summary + Deposit + Submit ────────────── */
            <div className="space-y-4" data-testid="wizard-step-3">
              <p className="font-heading text-base text-navy">Review Your Request</p>

              <div className="bg-bone rounded-sm p-4 space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Event</span>
                  <span className="text-navy font-medium text-right max-w-[60%]">{form.purpose}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Date</span>
                  <span className="text-navy font-medium">{form.date} ({getDayLabel(dateObj)})</span>
                </div>
                {form.time && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Time</span>
                    <span className="text-navy">{form.time}</span>
                  </div>
                )}
                {form.guests && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Guests</span>
                    <span className="text-navy">{form.guests}</span>
                  </div>
                )}
                <div className="border-t border-bone-dark pt-2 flex justify-between">
                  <span className="text-gray-500">Rental Fee</span>
                  <span className="text-gold font-heading text-lg font-semibold">${price}</span>
                </div>
              </div>

              <div className="bg-bone rounded-sm p-4 space-y-1 text-sm">
                <p className="text-navy font-medium">{form.first_name} {form.last_name}</p>
                {form.organization && <p className="text-gray-500">{form.organization}</p>}
                <p className="text-gray-500">{form.street}{form.street2 ? `, ${form.street2}` : ""}</p>
                <p className="text-gray-500">{form.city}, {form.state} {form.zip}</p>
                <p className="text-gray-500">{form.phone} &middot; {form.email}</p>
              </div>

              {/* ── Deposit Info ────────────────── */}
              <div className="border-t border-bone-dark pt-4">
                <div className="bg-bone rounded-sm p-4 text-sm text-gray-600">
                  <p className="text-navy font-medium mb-1">$50 Online Deposit Required</p>
                  <p className="text-gray-500 text-xs">After approval, you'll sign the rental agreement and pay a $50 non-refundable deposit securely online (PayPal or credit/debit card) to confirm your date.</p>
                </div>
              </div>

              <div className="flex items-start gap-2 text-xs text-gray-500 bg-bone rounded-sm p-3 border border-gold/20">
                <AlertTriangle className="h-3.5 w-3.5 text-gold mt-0.5 shrink-0" />
                <span>No payment is taken now. Once approved, you'll sign the rental agreement, then pay the $50 deposit to receive your confirmation with WiFi and key pickup details.</span>
              </div>

              <div className="flex gap-3">
                <Button data-testid="wizard-back-3" variant="outline" className="flex-1 border-bone-dark text-navy" onClick={goBack} disabled={loading}>
                  <ArrowLeft className="h-4 w-4 mr-2" /> Back
                </Button>
                <Button data-testid="booking-submit-btn" className="flex-1 btn-gold py-3 uppercase tracking-wider font-medium" onClick={submitRentalRequest} disabled={loading}>
                  {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  {loading ? "Submitting..." : "Submit Request"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </Layout>
  );
}
